/**
 * Calendar event write path for the site calendar — create, edit, delete,
 * and the series-vs-occurrence confirmation flow that follows an edit,
 * move, or resize of a recurring event. Every mutation reports success via
 * a toast and reloads the site's calendar data afterward.
 *
 * CalendarView owns the dialog/selection state (selectedDate, editType,
 * seriesEditDialog) and passes it in; this hook is the single place that
 * dispatches the create/edit/delete thunks and decides when a change to a
 * recurring event needs the series-choice dialog instead of writing
 * immediately.
 *
 * Contains:
 * - useCalendarEventMutations — hook (default export)
 * - INITIAL_SERIES_DIALOG_STATE — closed seriesEditDialog shape (named export)
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { createEvent, deleteEvent, editEvent } from '@sophocles/features/calendar/api';
import { setSelectedEventId } from '@sophocles/features/calendar/slice';
import { addToast } from '@sophocles/features/toast';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';

export const INITIAL_SERIES_DIALOG_STATE = {
  open: false,
  isRecurring: false,
  actionType: 'edit',
  eventId: null,
  formValues: null,
  occurrenceDate: null,
};

/**
 * @param {object} state
 * @param {number} state.siteId - the site whose calendar these writes target
 * @param {Array} state.events - current site events, for structural-change diffing against a recurring edit
 * @param {string|null} state.selectedDate
 * @param {(date: string|null) => void} state.setSelectedDate
 * @param {string|null} state.editType
 * @param {(type: string|null) => void} state.setEditType
 * @param {(open: boolean) => void} state.setIsEventDialogOpen
 * @param {object} state.seriesEditDialog
 * @param {(next: object) => void} state.setSeriesEditDialog
 * @param {() => void} state.loadData
 * @param {() => void} state.loadDataDebounced
 * @returns {{ commandLocked: boolean, onSubmit: Function, onDelete: Function, handleSeriesEditConfirm: Function }}
 */
export default function useCalendarEventMutations({
  siteId,
  events,
  selectedDate,
  setSelectedDate,
  editType,
  setEditType,
  setIsEventDialogOpen,
  seriesEditDialog,
  setSeriesEditDialog,
  loadData,
  loadDataDebounced,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);

  const runEventWrite = useCallback(
    async action => {
      const outcome = await runWrite(() => dispatch(action).unwrap(), { requiresCommand: true });
      return outcome.executed;
    },
    [dispatch, runWrite],
  );

  const onSubmit = useCallback(
    async (values, options = {}) => {
      if (commandLocked) return false;

      if (options.requiresSeriesChoice) {
        setSeriesEditDialog({
          open: true,
          isRecurring: true,
          actionType: 'edit',
          eventId: values.id,
          formValues: values,
          occurrenceDate: selectedDate,
        });
        return false;
      }

      if (editType === 'recurring' && values.id) {
        const originalEvent = events.find(e => e.id.toString() === values.id.toString());
        const isStructuralChange =
          originalEvent &&
          (values.start_date !== originalEvent.start_date ||
            values.end_date !== originalEvent.end_date ||
            values.recurrence?.parsed?.freq !== originalEvent.recurrence?.parsed?.freq ||
            values.recurrence?.parsed?.interval !== originalEvent.recurrence?.parsed?.interval ||
            values.title !== originalEvent.title ||
            values.relay?.id !== originalEvent.relay?.id);

        if (isStructuralChange) {
          const payload = {
            ...values,
            siteId,
            edit_type: 'series',
            ...(selectedDate && { occurrence_date: selectedDate }),
          };

          try {
            const executed = await runEventWrite(editEvent(payload));
            if (!executed) return false;
            dispatch(
              addToast({
                title: t('calendar.success'),
                message: t('calendar.eventSeriesUpdated'),
                level: 'success',
                dismissible: true,
              }),
            );
            setIsEventDialogOpen(false); // Close dialog on success
            dispatch(setSelectedEventId(null)); // Clear selected event
            setSelectedDate(null); // Clear selected date
            return true;
          } finally {
            loadData();
            setEditType(null);
          }
        } else {
          setSeriesEditDialog({
            open: true,
            isRecurring: true,
            actionType: 'edit',
            eventId: values.id,
            formValues: values,
            occurrenceDate: selectedDate,
          });
          return false;
        }
      }

      const action = values.id ? editEvent : createEvent;
      const message = values.id ? t('calendar.eventUpdatedSuccess') : t('calendar.eventCreatedSuccess');

      const payload = {
        ...values,
        siteId,
      };

      try {
        const executed = await runEventWrite(action(payload));
        if (!executed) return false;
        dispatch(
          addToast({
            title: t('calendar.success'),
            message,
            level: 'success',
            dismissible: true,
          }),
        );
        setIsEventDialogOpen(false); // Close dialog on success
        dispatch(setSelectedEventId(null)); // Clear selected event
        setSelectedDate(null); // Clear selected date
        return true;
      } finally {
        loadData();
        setEditType(null); // Reset edit type
      }
    },
    [
      commandLocked,
      dispatch,
      editType,
      events,
      loadData,
      runEventWrite,
      selectedDate,
      setEditType,
      setIsEventDialogOpen,
      setSelectedDate,
      setSeriesEditDialog,
      siteId,
      t,
    ],
  );

  const onDelete = useCallback(
    async eventId => {
      if (commandLocked) return false;
      try {
        const executed = await runEventWrite(deleteEvent({ siteId, eventId }));
        if (!executed) return false;
        dispatch(
          addToast({
            title: t('calendar.success'),
            message: t('calendar.eventDeletedSuccess'),
            level: 'success',
            dismissible: true,
          }),
        );
        return true;
      } finally {
        loadData();
      }
    },
    [commandLocked, dispatch, loadData, runEventWrite, siteId, t],
  );

  const handleSeriesEditConfirm = useCallback(
    async editType => {
      if (commandLocked) return false;

      const eventId = seriesEditDialog.eventId;
      const isDelete = seriesEditDialog.actionType === 'delete';
      const formValues = seriesEditDialog.formValues;
      const occurrenceDate = seriesEditDialog.occurrenceDate || selectedDate;

      if (isDelete) {
        const deletePayload = {
          siteId,
          eventId,
          edit_type: editType,
          ...(editType === 'occurrence' && occurrenceDate && { occurrence_date: occurrenceDate }),
        };

        try {
          const executed = await runEventWrite(deleteEvent(deletePayload));
          if (!executed) return false;
          dispatch(
            addToast({
              title: t('calendar.success'),
              message: t('calendar.eventDeletedSuccess'),
              level: 'success',
              dismissible: true,
            }),
          );
        } finally {
          loadDataDebounced();
        }
      } else if (formValues) {
        const payload = {
          ...formValues,
          siteId,
          edit_type: editType, // 'occurrence' or 'series'
          ...(editType === 'occurrence' && occurrenceDate && { occurrence_date: occurrenceDate }),
        };

        if (payload.recurrence && payload.recurrence.repeat_type === 'never') {
          payload.recurrence = null;
        }

        const isDragResize = seriesEditDialog.actionType === 'dragResize';
        const message = isDragResize ? t('calendar.eventMovedSuccess') : t('calendar.eventUpdatedSuccess');

        try {
          const executed = await runEventWrite(editEvent(payload));
          if (!executed) return false;
          dispatch(
            addToast({
              title: t('calendar.success'),
              message,
              level: 'success',
              dismissible: true,
            }),
          );
          if (!isDragResize) {
            setIsEventDialogOpen(false);
            setEditType(null);
          }
        } finally {
          loadDataDebounced();
          if (isDragResize) {
            setEditType(null);
          } else {
            dispatch(setSelectedEventId(null));
            setSelectedDate(null);
          }
        }
      }

      setSeriesEditDialog(INITIAL_SERIES_DIALOG_STATE);
      return true;
    },
    [
      commandLocked,
      dispatch,
      loadDataDebounced,
      runEventWrite,
      selectedDate,
      seriesEditDialog,
      setEditType,
      setIsEventDialogOpen,
      setSelectedDate,
      setSeriesEditDialog,
      siteId,
      t,
    ],
  );

  return useMemo(
    () => ({ commandLocked, onSubmit, onDelete, handleSeriesEditConfirm }),
    [commandLocked, onSubmit, onDelete, handleSeriesEditConfirm],
  );
}
